import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import axios from "axios";

// i18n initialization - must be imported before components
import './i18n';

// Pages
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import EmailVerificationPage from "./pages/EmailVerificationPage";
import AuthCallback from "./pages/AuthCallback";
import MarketplacePage from "./pages/MarketplacePage";
import ProductDetailPage from "./pages/ProductDetailPage";
import TariffCalculatorPage from "./pages/TariffCalculatorPage";
import DashboardPage from "./pages/DashboardPage";
import OnboardingPage from "./pages/OnboardingPage";
import DocumentsPage from "./pages/DocumentsPage";
import LogisticsPage from "./pages/LogisticsPage";
import TradeFinancePage from "./pages/TradeFinancePage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import ProductListingPage from "./pages/ProductListingPage";
import OrderManagementPage from "./pages/OrderManagementPage";
import OrderDetailPage from "./pages/OrderDetailPage";

// Buyer Pages
import BuyerDashboard from "./pages/BuyerDashboard";
import BuyerOnboarding from "./pages/BuyerOnboarding";
import BuyerInquiries from "./pages/BuyerInquiries";
import SellerInquiries from "./pages/SellerInquiries";

// Role-specific Dashboards (lazy loaded)
const LogisticsProviderDashboard = React.lazy(() => import("./pages/LogisticsProviderDashboard"));
const FinanceInstitutionDashboard = React.lazy(() => import("./pages/FinanceInstitutionDashboard"));
const GovernmentDashboard = React.lazy(() => import("./pages/GovernmentDashboard"));
const GovernmentCoOReviewPage = React.lazy(() => import("./pages/GovernmentCoOReviewPage"));
const AdminDashboard = React.lazy(() => import("./pages/AdminDashboard"));
const SharedReportPage = React.lazy(() => import("./pages/SharedReportPage"));

// Rules of Origin Pages (lazy loaded)
const RulesOfOriginPage = React.lazy(() => import("./pages/RulesOfOriginPage"));
const OriginAssessmentPage = React.lazy(() => import("./pages/OriginAssessmentPage"));
const OriginVaultPage = React.lazy(() => import("./pages/OriginVaultPage"));
const CoOApplicationPage = React.lazy(() => import("./pages/CoOApplicationPage"));

// Trade Compliance Engine
const TradeComplianceEngine = React.lazy(() => import("./pages/TradeComplianceEngine"));
const MyReportsPage = React.lazy(() => import("./pages/MyReportsPage"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Sign out immediately if an admin suspends or deactivates the account
axios.interceptors.response.use(undefined, (error) => {
  const detail = error.response?.data?.detail;
  if (error.response?.status === 403 && typeof detail === 'string' && detail.startsWith('Your account has been') && localStorage.getItem('atff_token')) {
    localStorage.removeItem('atff_token');
    localStorage.removeItem('atff_user');
    sessionStorage.setItem('atff_account_notice', detail);
    if (window.location.pathname !== '/login') window.location.assign('/login');
  }
  return Promise.reject(error);
});
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  // Try to restore user from localStorage immediately
  const storedUser = localStorage.getItem("atff_user");
  const [user, setUser] = useState(storedUser ? JSON.parse(storedUser) : null);
  const [token, setToken] = useState(localStorage.getItem("atff_token"));
  // Only loading if we have a token but no user yet
  const [loading, setLoading] = useState(!!localStorage.getItem("atff_token") && !storedUser);
  const [authReady, setAuthReady] = useState(!!storedUser);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Get fresh token from localStorage in case it was just set
    const currentToken = localStorage.getItem("atff_token");
    
    if (!currentToken) {
      setUser(null);
      setToken(null);
      localStorage.removeItem("atff_user");
      setLoading(false);
      setAuthReady(true);
      return;
    }

    // If we already have user data, skip the API call unless it's stale
    const cachedUser = localStorage.getItem("atff_user");
    if (cachedUser) {
      setUser(JSON.parse(cachedUser));
      setToken(currentToken);
      setLoading(false);
      setAuthReady(true);
      // Optionally refresh in background
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${currentToken}` };
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
      
      const response = await axios.get(`${API}/auth/me`, {
        headers,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      setUser(response.data);
      setToken(currentToken);
      // Cache user data
      localStorage.setItem("atff_user", JSON.stringify(response.data));
    } catch (error) {
      // Only clear auth if it's a 401/403, not a network/abort error
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        setUser(null);
        setToken(null);
        localStorage.removeItem("atff_token");
        localStorage.removeItem("atff_user");
      } else if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        // Keep the user logged in if it's just a network hiccup but token exists
        console.log('Auth check error (keeping session):', error.message);
      }
    } finally {
      setLoading(false);
      setAuthReady(true);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Re-check auth when token changes (e.g., after Google login)
  useEffect(() => {
    if (token && !user && authReady) {
      checkAuth();
    }
  }, [token, user, authReady, checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    setUser(userData);
    localStorage.setItem("atff_token", access_token);
    localStorage.setItem("atff_user", JSON.stringify(userData));
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    setToken(access_token);
    setUser(newUser);
    localStorage.setItem("atff_token", access_token);
    localStorage.setItem("atff_user", JSON.stringify(newUser));
    return newUser;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Legacy Emergent Auth - kept for backward compatibility
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Custom Google OAuth login handler - for ATFFtrade.africa branding
  const googleLogin = async (credential) => {
    try {
      const response = await axios.post(`${API}/auth/google`, { credential });
      const { access_token, user: userData } = response.data;
      
      // Store token and user first
      localStorage.setItem("atff_token", access_token);
      localStorage.setItem("atff_user", JSON.stringify(userData));
      
      // Then update state
      setToken(access_token);
      setUser(userData);
      
      // Force a small delay to ensure state is updated before navigation
      await new Promise(resolve => setTimeout(resolve, 100));
      
      return userData;
    } catch (error) {
      console.error("Google login error:", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem("atff_token");
    localStorage.removeItem("atff_user");
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    loginWithGoogle,
    googleLogin,
    logout,
    setUser,
    setToken,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, token } = useAuth();
  const location = useLocation();
  
  // Also check localStorage directly as a fallback
  const hasStoredToken = !!localStorage.getItem("atff_token");

  // Add a maximum loading time failsafe (5 seconds)
  const [timedOut, setTimedOut] = useState(false);
  
  useEffect(() => {
    if (loading) {
      const timeout = setTimeout(() => {
        setTimedOut(true);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [loading]);

  if (loading && !timedOut) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-continental border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // Check both React state and localStorage
  if (!isAuthenticated && !hasStoredToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment (not query params) for session_id synchronously
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/shared-report/:token" element={
        <React.Suspense fallback={<div className="min-h-screen" />}>
          <SharedReportPage />
        </React.Suspense>
      } />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email" element={<EmailVerificationPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/marketplace" element={<MarketplacePage />} />
      <Route path="/products/:productId" element={<ProductDetailPage />} />
      <Route path="/tariff-calculator" element={<TariffCalculatorPage />} />
      
      {/* Trade Compliance Engine */}
      <Route path="/compliance-engine" element={
        <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
          <TradeComplianceEngine />
        </React.Suspense>
      } />
      <Route path="/my-reports" element={
        <ProtectedRoute>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
            <MyReportsPage />
          </React.Suspense>
        </ProtectedRoute>
      } />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute>
          <DocumentsPage />
        </ProtectedRoute>
      } />
      <Route path="/logistics" element={
        <ProtectedRoute>
          <LogisticsPage />
        </ProtectedRoute>
      } />
      <Route path="/trade-finance" element={
        <ProtectedRoute>
          <TradeFinancePage />
        </ProtectedRoute>
      } />
      
      {/* Buyer Routes */}
      <Route path="/buyer/dashboard" element={
        <ProtectedRoute>
          <BuyerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/buyer/onboarding" element={
        <ProtectedRoute>
          <BuyerOnboarding />
        </ProtectedRoute>
      } />
      <Route path="/buyer/inquiries" element={
        <ProtectedRoute>
          <BuyerInquiries />
        </ProtectedRoute>
      } />
      <Route path="/buyer/inquiries/:inquiryId" element={
        <ProtectedRoute>
          <BuyerInquiries />
        </ProtectedRoute>
      } />
      <Route path="/buyer/orders" element={
        <ProtectedRoute>
          <OrderManagementPage />
        </ProtectedRoute>
      } />
      <Route path="/buyer/orders/:orderId" element={
        <ProtectedRoute>
          <OrderDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/buyer/saved-suppliers" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      
      {/* SME Routes */}
      <Route path="/my-products" element={
        <ProtectedRoute>
          <ProductListingPage />
        </ProtectedRoute>
      } />
      <Route path="/products/edit/:productId" element={
        <ProtectedRoute>
          <ProductListingPage />
        </ProtectedRoute>
      } />
      <Route path="/orders" element={
        <ProtectedRoute>
          <OrderManagementPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/:orderId" element={
        <ProtectedRoute>
          <OrderDetailPage />
        </ProtectedRoute>
      } />
      {/* SME Inquiries (Seller view) */}
      <Route path="/inquiries" element={
        <ProtectedRoute>
          <SellerInquiries />
        </ProtectedRoute>
      } />
      <Route path="/inquiries/:inquiryId" element={
        <ProtectedRoute>
          <SellerInquiries />
        </ProtectedRoute>
      } />
      
      {/* Logistics Provider Routes */}
      <Route path="/logistics/dashboard" element={
        <ProtectedRoute>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
            <LogisticsProviderDashboard />
          </React.Suspense>
        </ProtectedRoute>
      } />
      <Route path="/logistics/onboarding" element={
        <ProtectedRoute>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
            <LogisticsProviderDashboard />
          </React.Suspense>
        </ProtectedRoute>
      } />
      
      {/* Financial Institution Routes */}
      <Route path="/finance/dashboard" element={
        <ProtectedRoute>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
            <FinanceInstitutionDashboard />
          </React.Suspense>
        </ProtectedRoute>
      } />
      <Route path="/finance/onboarding" element={
        <ProtectedRoute>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
            <FinanceInstitutionDashboard />
          </React.Suspense>
        </ProtectedRoute>
      } />
      
      {/* Government/National Node Routes */}
      <Route path="/government/dashboard" element={
        <ProtectedRoute>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
            <GovernmentDashboard />
          </React.Suspense>
        </ProtectedRoute>
      } />
      <Route path="/government/onboarding" element={
        <ProtectedRoute>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
            <GovernmentDashboard />
          </React.Suspense>
        </ProtectedRoute>
      } />
      <Route path="/government/coo-review" element={
        <ProtectedRoute>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
            <GovernmentCoOReviewPage />
          </React.Suspense>
        </ProtectedRoute>
      } />
      
      {/* Super Admin Routes */}
      <Route path="/admin/dashboard" element={
        <ProtectedRoute>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
            <AdminDashboard />
          </React.Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
            <AdminDashboard />
          </React.Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin/businesses" element={
        <ProtectedRoute>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
            <AdminDashboard />
          </React.Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin/nodes" element={
        <ProtectedRoute>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
            <AdminDashboard />
          </React.Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin/verifications" element={
        <ProtectedRoute>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
            <AdminDashboard />
          </React.Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
            <AdminDashboard />
          </React.Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin/logistics" element={
        <ProtectedRoute>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
            <AdminDashboard />
          </React.Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin/finance" element={
        <ProtectedRoute>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
            <AdminDashboard />
          </React.Suspense>
        </ProtectedRoute>
      } />
      
      {/* Rules of Origin Routes */}
      <Route path="/rules-of-origin" element={
        <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
          <RulesOfOriginPage />
        </React.Suspense>
      } />
      <Route path="/origin/assessment" element={
        <ProtectedRoute>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
            <OriginAssessmentPage />
          </React.Suspense>
        </ProtectedRoute>
      } />
      <Route path="/origin/vault" element={
        <ProtectedRoute>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
            <OriginVaultPage />
          </React.Suspense>
        </ProtectedRoute>
      } />
      <Route path="/coo" element={
        <ProtectedRoute>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
            <CoOApplicationPage />
          </React.Suspense>
        </ProtectedRoute>
      } />
      <Route path="/coo/:applicationId" element={
        <ProtectedRoute>
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-continental border-t-transparent rounded-full animate-spin"></div></div>}>
            <CoOApplicationPage />
          </React.Suspense>
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
